// src/components/Decor/VideoBackdrop.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";

export default function VideoBackdrop({
  srcWebm,
  srcMp4,
  poster,
  overlayColor = "black",
  overlayOpacity = 0.45,
  className = "",
  zIndex = 0,            // sits above body, below page content (which should use z-10)
  forceMotion = false,   // ignore prefers-reduced-motion (for testing)
  fadeMs = 600,          // fade-in duration for the video
  fadeDelayMs = 80,      // small delay to avoid flash of poster switch
}) {
  const videoRef = useRef(null);
  const [allowMotion, setAllowMotion] = useState(true);
  const [isReady, setIsReady] = useState(false); // when video has enough data to play

  // Respect reduced motion (for playback + fade)
  useEffect(() => {
    if (forceMotion) { setAllowMotion(true); return; }
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setAllowMotion(!mq?.matches);
    updateMotion();
    mq?.addEventListener?.("change", updateMotion);
    return () => mq?.removeEventListener?.("change", updateMotion);
  }, [forceMotion]);

  // Pause on tab hidden, resume on visible
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) v.pause();
      else if (allowMotion) v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [allowMotion]);

  // Fade style (opacity animates once the video can play)
  const fadeStyle = useMemo(() => {
    const duration = allowMotion ? fadeMs : 0;
    const delay = allowMotion ? fadeDelayMs : 0;
    return {
      opacity: isReady ? 1 : 0,
      transition: `opacity ${duration}ms ease-out ${delay}ms`,
      willChange: "opacity",
    };
  }, [isReady, allowMotion, fadeMs, fadeDelayMs]);

  const wrapStyle = {
    position: "fixed",
    inset: 0,
    zIndex,
    overflow: "hidden",
    pointerEvents: "none",
  };

  const baseMediaStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "translateZ(0)", // GPU hint
  };

  const overlayStyle = {
    position: "absolute",
    inset: 0,
    background: overlayColor,
    opacity: overlayOpacity,
  };

  // If user prefers reduced motion (and not forceMotion), show poster only
  if (!allowMotion) {
    return (
      <div className={className} style={wrapStyle} aria-hidden>
        {poster ? <img src={poster} alt="" style={baseMediaStyle} /> : null}
        <div style={overlayStyle} />
      </div>
    );
  }

  return (
    <div className={className} style={wrapStyle} aria-hidden>
      {/* Poster layer: shows immediately under the overlay */}
      {poster ? (
        <img
          src={poster}
          alt=""
          style={{ ...baseMediaStyle, position: "absolute", inset: 0 }}
          aria-hidden
        />
      ) : null}

      {/* Video layer: fades in on ready, sits above poster but below overlay */}
      <video
        ref={videoRef}
        style={{ ...baseMediaStyle, ...fadeStyle, position: "absolute", inset: 0 }}
        playsInline
        autoPlay
        muted
        loop
        preload="auto"
        poster={poster}
        onLoadedData={() => {
          // mark ready and attempt play once sources are ready (helps iOS/Safari)
          setIsReady(true);
          videoRef.current?.play().catch(() => {});
        }}
        onCanPlay={() => {
          setIsReady(true);
          videoRef.current?.play().catch(() => {});
        }}
      >
        {srcWebm ? <source src={srcWebm} type="video/webm" /> : null}
        {srcMp4 ? <source src={srcMp4} type="video/mp4" /> : null}
      </video>

      {/* Dark overlay (on top of both poster + video) */}
      <div style={overlayStyle} />
    </div>
  );
}
